import { AnnouncementDB } from './announcement.types';
import { CustomPage } from './custom-page.types';
import { DesignShape, DesignStyle } from './design.types';
import { eCDNConfiguration, eCDNData } from './ecdn.types';
import { ChannelFeaturesEnum } from './feature-flags.types';
import { IFeedbackSurveyText } from './feedback-survey.types';
import { GDPRSettings } from './gdpr.types';
import { DBEventHomepage, ENavbarTypes, EventHomepage, IHomepageMainNavItems } from './homepage.types';
import { I18NSettings } from './i18n.types';
import { LeaderboardSettings } from './leaderboard.types';
import { PageGating, Registration, RegistrationQuestion, RegistrationStep } from './registration.types';
import { DBSession, Session, SessionPlaybackVideo, SessionPreview } from './session.types';
import { SocialSettings } from './socials.types';
import { ITemplateEventTheme, Template } from './template.types';
import * as ThemePackTypes from './theme-packs.types';
import { TranslateString } from './translation.types';
import { LiveVideoMap, SecondaryVideoMap } from './video.types';
import { TextOverrides } from './content-overrides.types';

export type TProjectUuid = string;

export interface BrandliveEvent {
  event: number;
  channel: number; //client id still in mysql - not migrating
  status: EProjectStatus; // 0 = deleted, 1 = public, 2 = private
  template: Template;
  name: string;
  settings: EventSettings;
  registration_settings?: Registration;
  // the original use of BrandliveEvent.registration_questions was deprecated
  // it is now being used to consolidate for reference all questions asked during registration and all fields shown in user profiles
  registration_questions: RegistrationQuestion[];
  registration_steps?: RegistrationStep[];
  required_registration_questions: number[] | null;
  registration_on: boolean;
  homepage: EventHomepage | null;
  sessions: Session[];
  uuid: TProjectUuid;
  created_date: Date;
  terms_id: number;
  privacy_policy: number;
  sessionsPreviews?: SessionPreview[];
  custom_css: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  channel_settings?: any;
  social_sharing?: {
    image?: string;
    name?: string;
    description?: string;
  };
  playbackUrls?: LiveVideoMap;
  channel_logos?: ILogos;
  favicon?: string | null;
  paidSessions?: string[];
  custom_pages?: CustomPage[];
  secondaryVideos?: SecondaryVideoMap;
  social_settings?: SocialSettings;
  custom_css_compiled: string | null;
  use_safe_css: boolean; // default false in database, will always be defined
  event_groups?: string[];
  modified_at: null | string;
  translation_manifests: Record<string, string>;
  tags?: IEventTag[];
  automatic_session_filter_by_user_language?: boolean;
  event_preview: EventPreview | null;
  is_preview: boolean | null;
  channel_event_icons?: ChannelCustomizedIcon[];
  is_template?: boolean;
  associated_event_groups?: EventGroupAssociation[];
  channel_features: ChannelFeaturesEnum[];
  event_type?: EventType;
  event_image?: string;
  event_description?: string;
  template_theme?: ITemplateEventTheme;
  is_module_grouping_v2?: boolean;
  announcements?: AnnouncementDB[];
  dma_id?: string | null;
  is_publish_locked?: boolean;
  is_test_event?: boolean;
  ecdn_data?: eCDNData[];
  ecdn_configuration?: eCDNConfiguration;
  sessionVideos?: SessionPlaybackVideo[];
  placeholderEndpoints?: SecondaryVideoMap;
  global_template_type?: string | null;
  /** projects now have optional start/end times - these are strictly superficial (for the moment) and have no effect on streams */
  start_timestamp?: number | null;
  end_timestamp?: number | null;
  /** categories is a new project level list of categories - completely separate from session level categories (tracks) */
  categories?: string[] | null;
  btv_channel_categories?: number[] | null;
  originating_template_name?: string | null;
  name_translations?: TranslateString | null;
  // New parameters must be optional or nullable
}

// the events list is expensive to query, so we only return the fields we need to reduce
// the payload size
export interface SelectedBrandliveEvent
  extends Pick<
    BrandliveEvent,
    | 'event'
    | 'channel'
    | 'status'
    | 'template'
    | 'name'
    | 'settings'
    | 'registration_settings'
    | 'registration_on'
    | 'homepage'
    | 'sessions'
    | 'registration_questions'
    | 'uuid'
    | 'created_date'
    | 'tags'
    | 'is_template'
    | 'event_type'
    | 'event_image'
    | 'event_description'
    | 'is_publish_locked'
    | 'is_test_event'
    | 'dma_id'
    | 'template_theme'
  > {
  _sessions: Session[] | null;
  _homepage: EventHomepage | null;
  sessionsPreviews: undefined;
  registration_settings: Omit<Registration, 'registration_confirmation_email'>;
  series_uuid?: string | null;
  // New parameters must be optional or nullable
}

/* Do not re-order the status enum */
export enum EProjectStatus {
  DELETED,
  PUBLIC,
  PRIVATE,
}

export interface EventSettings {
  design: IEventDesignSettings;
  /**
   * The project uuid of the "template," aka event, used (if any) when creating the new project.
   *
   * It is an array of project uuids because a project can be creted from a template, and then turned into a template itself
   * so this will track the lineage of the project.
   * */
  created_from_template_project?: string[];
  branding?: {
    removeBrandliveLogo: boolean;
    customURL: string;
    favicon: string;
  };
  customCSS?: string;
  email?: {
    sender?: string;
    colors?: ThemePackTypes.ThemePack;
    hideBrandliveLogo?: boolean;
    color_palette_mode?: ThemePackTypes.EPaletteModes;
    font?: ThemePackTypes.SafeFonts;
    detachedEmailFromEventThemePack?: boolean;
    // When the subject is manually overridden, *never* default to the event name as the subject.
    registrationConfirmationSubjectChanged?: boolean;
  };
  event_navbar?: EventNavbar;
  display_session_footer?: boolean;
  display_sessions?: boolean;
  display_landing_page?: boolean;
  display_homepage?: boolean;
  max_featured_sessions?: MaxFeaturedType;
  max_featured_speakers?: MaxFeaturedType;
  max_featured_products?: MaxFeaturedType;
  pageGating?: PageGating;
  personalizedAttendeeList: boolean;
  show_add_to_cal_btn?: boolean;
  personalizedAttendeeListModal: boolean;
  vodOrder?: number[] | null;
  content_editor?: ContentEditors;
  attendeeInPersonEnabled?: boolean;
  i18n?: I18NSettings;
  translation_version?: number;
  display_session_language_dropdown?: boolean;
  disable_session_thumbnails?: boolean;
  leaderboard_settings?: LeaderboardSettings;
  feedback_survey_text?: IFeedbackSurveyText;
  gdpr?: GDPRSettings;
  description?: string;
  template?: string;
  is_module_grouping_v2?: boolean;
  is_registration_v2?: boolean;
  invert_display_agenda_toggle?: boolean;
  tracks_translations?: IEventTracksTranslations;
  pm_managed?: boolean;
  useMux?: boolean;
  course_enabled?: boolean;
  microsoft_ecdn_enabled?: boolean;
  is_auto_generated_name?: boolean;
  useSeries?: boolean;
  watermark_set?: string;
  canonicalTimezone?: string; // this is for display-purposes only
  // New parameters must be optional or nullable
}

export interface IEventDesignSettings {
  /** color_theme is the theme used for the color pack, light/dark, etc. or potentially a custom theme set by users  */
  color_theme?: ThemePackTypes.EPaletteModes;
  /** if this is true, then all pages (including sessions) use the event settings color_theme */
  use_project_theme?: boolean;
  colors: ThemePackTypes.ThemePack;
  style: {
    style: DesignStyle;
    shape: DesignShape;
  };
  /** @deprecated in favor of theme packs. Please use design.colors.font_pack, if it doesn't exist you may try and fallback to this. */
  font: ThemePackTypes.FontPack;
  /** @deprecated in favor of theme packs. Please use design.colors.custom_styles, if it doesn't exist you may try and fallback to this. */
  custom?: ThemePackTypes.ICustomDesignSettings;
  /** timestamp of when the theme pack changes for the project */
  theme_pack_modified_at?: number | null;
}

export interface ILogos {
  white: string | null;
  color: string | null;
  gray: string | null;
  favicon: string | null;
  watermark: string | null;
  // New parameters must be optional or nullable
  channel_image?: string | null;
}

export interface IEventTag {
  uuid: string;
  name: string;
  color: string;
}

export interface EventPreview {
  uuid: string;
  password: string;
  url: string;
}

export interface ChannelCustomizedIcon {
  channel: number;
  name: string;
  url: string;
  uuid?: string;
  status?: number;
}

export interface EventsTotals {
  live: number;
  ended: number;
  upcoming: number;
  on_demand: number;
  total: number;
  on_demand_including_ended_sessions: number;
  test_events: number;
  total_template_events?: number;
  course_events?: number;
}

export interface EventGroup {
  uuid: string;
  name: string;
  channel?: number;
  event_uuids: string[];
  adminIds: number[];
}

export interface EventGroupAssociation {
  name?: string;
  channel?: number;
  id?: number;
  created_at?: string;
  group_uuid: string;
  event_uuid: string;
}

/** EventType are all the possible types a project might have, including legacy types */
export enum EventType {
  Webinar = 'Webinar',
  Event = 'Event',
  OnDemand = 'OnDemand',
  Allhands = 'Allhands',
  Townhall = 'Townhall',
  Hub = 'Hub',
  Other = 'Other',
}

export const EventTypeLabels: Partial<Record<EventType, { name: string; description: string }>> = {
  [EventType.Webinar]: {
    name: 'Webinar',
    description: 'Live presentation on a focused topic to an external audience',
  },
  [EventType.Event]: {
    name: 'Event',
    description: 'Multiple sessions to engage viewers',
  },
  [EventType.Townhall]: {
    name: 'Townhall',
    description: 'Internal forum for updates and discussion',
  },
  [EventType.OnDemand]: {
    name: 'On Demand',
    description: 'Recorded session(s) available anytime',
  },
  [EventType.Hub]: { name: 'Hub', description: '' },
  [EventType.Other]: { name: 'Other', description: '' },
};

export interface EventNavbar {
  navbarItems: IHomepageMainNavItems[]; // not used now but we need a unique identifier for contorls.tsx and this will likely be useful for future use (similar to header)
  styling_overrides?: {
    background?: {
      color: string;
      image: {
        is_on: boolean;
        src: string;
      };
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content?: any;
  };
  navbarType?: ENavbarTypes;
  isTransparent?: boolean;
  isSticky?: boolean;
  blurLevel?: number;
  // New parameters must be optional or nullable
}

// Nullish max_featured values remove the limit such that either all will be shown or whatever the template default is
export type MaxFeaturedType = 3 | 6 | 12 | 24 | null;

export enum ContentEditors {
  quill = 'quill',
  draft = 'draft',
}

interface IEventTracksTranslations {
  [key: string]: TranslateString;
}

type DBBrandliveEventMedium = Omit<DBBrandliveEvent, 'homepage' | 'sessions'>;

export interface GetEventsResponse extends DBBrandliveEventMedium {
  homepage: DBEventHomepage | null;
  sessions: DBSession[] | [null];
  associated_event_groups?: EventGroup[];
  template_theme?: ITemplateEventTheme;
  custom_pages?: CustomPage[];
}

export interface DBBrandliveEvent {
  event?: number;
  channel: number; //client id still in mysql - not migrating
  status: 0 | 1 | 2; // 0 = deleted, 1 = public, 2 = private
  template: number;
  name: string;
  event_type: EventType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  settings?: any;
  registration_settings?: Registration;
  registration_questions?: number[];
  registration_on: boolean;
  registration_steps?: RegistrationStep[] | null;
  url?: string;
  homepage: number | null;
  sessions: number[];
  uuid: string;
  terms_id: number;
  privacy_policy: number;
  custom_css: string | null;
  social_settings: SocialSettings;
  custom_css_compiled: string | null;
  use_safe_css: boolean; //default false in database, will be defined
  translation_manifests: Record<string, string>;
  is_template: boolean; // default false in database, will always be defined
  event_image?: string;
  event_description?: string;
  global_template_type?: string | null;
  name_translations?: TranslateString | null;
  // New parameters must be optional or nullable
}

export interface IEventPreview {
  uuid: string;
  password: string;
  url: string;
}

export type BrandliveEventListItem = Pick<
  BrandliveEvent,
  'event' | 'name' | 'uuid' | 'registration_on' | 'required_registration_questions' | 'created_date'
>;

export interface TermsConditions {
  terms_id: number;
  channel: number;
  content: string;
  settings?: {
    content?: TextOverrides;
  };
  // New parameters must be optional or nullable
}

export interface PrivacyPolicy {
  privacy_policy: number;
  channel: number;
  content: string;
  settings?: {
    content?: TextOverrides;
  };
  // New parameters must be optional or nullable
}

export enum LoadingStatus {
  PreLoading = 'pre-loading',
  Loading = 'loading',
  Done = 'done',
  Error = 'error',
}

type GroupTreeGroupEntry = {
  name: string;
  uuid: string;
  events: Record<string, { name: string; uuid: string }>;
};

type GroupTreeUngroupedEntry = {
  events: Record<string, { name: string; uuid: string }>;
};

export type EventGroupTreeEntry = {
  ungrouped: GroupTreeUngroupedEntry;
  [key: string]: GroupTreeGroupEntry | GroupTreeUngroupedEntry;
};
